import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ServidorService } from '../servidor.service';
import { take } from 'rxjs';
import { RhNetService } from 'src/app/services/rhNet.service';
import { Servidor } from 'src/app/modules/admin/models/Servidor';
import { SnackBarService } from 'src/app/services/snack-bar.service';

@Component({
  selector: 'busca-cpf-form',
  templateUrl: './busca-cpf-form.component.html',
  styleUrls: ['./busca-cpf-form.component.css'],
})
export class BuscaCpfFormComponent {
  servidorLoading: boolean = false;

  constructor(
    private rhNet: RhNetService,
    private snackBar: SnackBarService
  ) {

  }

  @Input() control = new FormControl;
  @Input() requiredAttr = false;
  @Input() disabled = false;
  @Input() btnTooltip = 'Buscar dados por CPF';
  @Input() label = 'CPF';
  @Input() placeholder = 'CPF';
  @Input() appearance = 'outline';
  @Input() btnIcon = 'search';
  @Output()  servidor = new EventEmitter<Servidor>();

  getServidor() {
    this.control.disable();
    this.servidorLoading = true;
    this.rhNet.getServidorByCPF({ cpf: this.control.value })
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          // Verifica se são dados brutos (tem vinculos[].vinculosVaga[]) ou processados (tem vinculos[].regimeJuridico)
          const isDadosBrutos = res.vinculos && res.vinculos.length > 0 &&
                                res.vinculos[0].vinculosVaga && Array.isArray(res.vinculos[0].vinculosVaga);

          // Processa dados brutos se necessário
          const servidor: Servidor = isDadosBrutos ? this.processarDadosBrutosRHNet(res) : res;

          this.control.enable();
          this.servidorLoading = false;
          this.servidor.emit(servidor);
          this.snackBar.showSnackBarSuccess("CPF válido!");
        },
        error: error => {
          switch (error.error.code) {
            case 45:
              console.error(error);
              this.servidorLoading = false;
              this.control.enable();
              this.snackBar.showSnackBarError('Servidor não encontrado');
              break;
            default:
              console.error(error);
              this.servidorLoading = false;
              this.control.enable();
              this.snackBar.showSnackBarError('Erro ao conectar ao RHNet');
              break;
          }
        }
      })
  }

  keyPressSubmit(event) {
    event.key === "Enter" ? this.getServidor() : null;
  }

  /**
   * Processa dados brutos do RHNet e filtra apenas vínculos ativos (statusVinculoVaga == "S")
   * Transforma para o formato esperado (Servidor)
   */
  private processarDadosBrutosRHNet(dadosBrutos: any): Servidor {
    const vinculosAtivos = [];

    // Itera sobre todos os vínculos
    for (let i = 0; i < dadosBrutos.vinculos.length; i++) {
      const vinculo = dadosBrutos.vinculos[i];

      // Itera sobre os vínculos de vaga dentro de cada vínculo
      for (let j = 0; j < vinculo.vinculosVaga.length; j++) {
        const vinculoVaga = vinculo.vinculosVaga[j];

        // Filtra apenas vínculos ativos (statusVinculoVaga == "S")
        if (vinculoVaga.statusVinculoVaga == "S") {
          // Transforma para o formato esperado (VinculoServidor)
          vinculosAtivos.push({
            id: vinculoVaga.codigoVinculoVaga,
            ativo: true,
            regimeJuridico: {
              id: parseInt(vinculo.codigoRegimeJuridico),
              codigo: parseInt(vinculo.codigoRegimeJuridico),
              descricao: vinculo.descricaoRegimeJuridico
            },
            cargo: {
              id: parseInt(vinculoVaga.sequenciaCargo),
              codigo: parseInt(vinculoVaga.sequenciaCargo),
              descricao: vinculoVaga.descricaoCargo,
              grupo: {
                id: parseInt(vinculoVaga.codgGrupoCargo),
                codigo: parseInt(vinculoVaga.codgGrupoCargo),
                descricao: vinculoVaga.descGrupoCargo,
                escolaridade: null as any, // Será preenchido se necessário
                tipo: vinculoVaga.tipoGrupoCargo ? {
                  id: parseInt(vinculoVaga.tipoGrupoCargo),
                  codigo: parseInt(vinculoVaga.tipoGrupoCargo),
                  descricao: ''
                } : undefined
              }
            },
            funcao: vinculoVaga.codigoAtividadeFuncao != 0 ? {
              id: parseInt(vinculoVaga.codigoAtividadeFuncao),
              nome: vinculoVaga.descricaoAtividadeFuncao || ''
            } : null as any,
            orgao: {
              origem: {
                id: vinculoVaga.orgaoOrigemCodg,
                codigo: String(vinculoVaga.orgaoOrigemCodg),
                nome: vinculoVaga.orgaoOrigemDesc,
                sigla: undefined,
                central: false
              },
              lotacao: {
                id: parseInt(vinculo.codigoOrgao),
                codigo: vinculo.codigoOrgao,
                nome: vinculo.nomeOrgao,
                sigla: undefined,
                central: false
              }
            },
            unidadeAdmLotacao: {
              id: parseInt(vinculo.identificadorUnidadeAdministrativa),
              codigo: vinculo.identificadorUnidadeAdministrativa,
              descricao: vinculo.descricaoUnidadeAdministrativa
            }
          });
        }
      }
    }

    // Retorna no formato esperado
    return {
      id: null,
      nome: dadosBrutos.nome,
      cpf: dadosBrutos.cpf ? String(dadosBrutos.cpf).padStart(11, '0') : dadosBrutos.cpf,
      dataNascimento: dadosBrutos.dataNascimento,
      genero: dadosBrutos.siglaSexo === 'M' ? 'MASCULINO' : 'FEMININO',
      email: dadosBrutos.email || '',
      telefone: this.getTelefone(dadosBrutos.telefones) || '',
      estadoCivil: dadosBrutos.estadoCivil === 'C' ? 'CASADO' : 'SOLTEIRO',
      vinculos: vinculosAtivos,
      vinculo: vinculosAtivos.length > 0 ? vinculosAtivos[0] : undefined
    };
  }

  /**
   * Extrai telefone celular dos dados brutos do RHNet
   */
  private getTelefone(telefones: any[]): string | null {
    if (!telefones || telefones.length === 0) {
      return null;
    }

    for (const tel of telefones) {
      if (tel.tipoTelefoneDesc === 'celular') {
        return tel.ddd + tel.numero;
      }
    }

    // Se não encontrou celular, retorna o primeiro disponível
    return telefones[0] ? telefones[0].ddd + telefones[0].numero : null;
  }
}
